import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
    useCreateDroplistRelease,
    useDroplistRun,
    usePushToV3,
    useSaveProducts,
    useStartParse,
} from 'src/hooks/useDroplists'
import { DroplistBrand, DroplistConfig, DroplistProduct, DroplistRun } from 'src/types/Droplist'
import { droplistError } from 'src/utils/droplistApi'
import { notify } from 'src/utils/notify'
import styled from 'styled-components'
import {
    ActionButton,
    Badge,
    Banner,
    Card,
    CardTitle,
    DangerButton,
    ErrorText,
    InlineSpinner,
    Muted,
    PrimaryButton,
    Row,
    SecondaryButton,
    Spacer,
} from '../affiliate/ui'
import { Mono } from '../admin-affiliates/shared'
import ConfigFields, { configToSend, initialConfig } from './ConfigFields'
import ConfirmDialog from './ConfirmDialog'
import ProductsTable from './ProductsTable'

const when = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : ''

const LogBox = styled.div`
    max-height: 260px;
    overflow-y: auto;
    padding: 12px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    gap: 2px;
`

// A collection name moves to its own line rather than splitting mid-word.
const Collection = styled(Mono)`
    word-break: normal;
    overflow-wrap: anywhere;
`

// Stays at the bottom of the screen while there are unsaved edits, wherever in the list they were made.
const SaveBar = styled.div`
    position: sticky;
    bottom: 12px;
    z-index: 5;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 10px 10px 18px;
    border-radius: 18px;
    background: #23243a;
    border: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);

    > span {
        flex: 1;
        font-size: 14px;
        font-weight: 600;
    }

    @media (max-width: 700px) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        padding: 10px;

        > span {
            grid-column: 1 / -1;
            text-align: center;
        }
    }
`

const StatusBadge = ({ run }: { run: DroplistRun }) => {
    if (run.busy) return <Badge $tone="blue">{run.busy[0].toUpperCase() + run.busy.slice(1)}…</Badge>
    if (run.status === 'failed') return <Badge $tone="red">Failed</Badge>
    return <Badge $tone="green">{run.products.length} {run.products.length === 1 ? 'product' : 'products'}</Badge>
}

const RunLog = ({ run }: { run: DroplistRun }) => {
    const box = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(!!run.busy)
    useEffect(() => {
        if (run.busy) setOpen(true)
    }, [run.busy])
    useEffect(() => {
        if (box.current) box.current.scrollTop = box.current.scrollHeight
    }, [run.log.length, open])

    return (
        <Card>
            <Row>
                <CardTitle>Log</CardTitle>
                <Spacer />
                <SecondaryButton type="button" style={{ minHeight: 34, padding: '0 12px', fontSize: 13 }} onClick={() => setOpen(!open)}>
                    {open ? 'Hide' : `Show (${run.log.length})`}
                </SecondaryButton>
            </Row>
            {open && (
                <LogBox ref={box}>
                    {run.log.length === 0 ? (
                        <Mono>Starting…</Mono>
                    ) : (
                        run.log.map((line, i) => (
                            <Mono key={i} style={{ wordBreak: 'break-word' }}>
                                {new Date(line.at).toLocaleTimeString()} {line.message}
                            </Mono>
                        ))
                    )}
                </LogBox>
            )}
        </Card>
    )
}

const ParseCard = ({ brand, run }: { brand: DroplistBrand; run: DroplistRun | null }) => {
    const start = useStartParse(brand.id)
    const [config, setConfig] = useState<DroplistConfig>(() => initialConfig(brand.fields, run?.config))

    const submit = (e: FormEvent) => {
        e.preventDefault()
        start.mutate(configToSend(brand.fields, config), {
            onError: (err) => notify(droplistError(err), 'error'),
        })
    }

    return (
        <Card as="form" onSubmit={submit}>
            <Row>
                <CardTitle>Parse {brand.name}</CardTitle>
                {run && <StatusBadge run={run} />}
            </Row>
            <ConfigFields fields={brand.fields} value={config} onChange={setConfig} />
            <Row>
                <ActionButton type="submit" disabled={start.isPending || !!run?.busy}>
                    {run?.busy === 'parsing' ? 'Parsing…' : run ? 'Parse again' : 'Run parser'}
                </ActionButton>
                {run?.parsedAt && <Muted>Last parsed {when(run.parsedAt)}</Muted>}
            </Row>
        </Card>
    )
}

const ProductsCard = ({
    brand,
    run,
    draft,
    setDraft,
}: {
    brand: DroplistBrand
    run: DroplistRun
    draft: DroplistProduct[] | null
    setDraft: (draft: DroplistProduct[] | null) => void
}) => {
    const save = useSaveProducts(brand.id)
    const products = draft ?? run.products

    // Products are stored by name in V3, so a repeated name keeps only the last one.
    const duplicates = useMemo(() => {
        const seen = new Map<string, number>()
        for (const p of products) {
            const id = p.name.replaceAll('/', ' ').trim()
            seen.set(id, (seen.get(id) ?? 0) + 1)
        }
        return Array.from(seen.entries()).filter(([, n]) => n > 1).map(([name]) => name)
    }, [products])

    const saveDraft = () => {
        if (!draft) return
        save.mutate(
            { runId: run.id, products: draft },
            {
                onSuccess: () => {
                    setDraft(null)
                    notify('Changes saved.', 'success')
                },
                onError: (err) => notify(droplistError(err), 'error'),
            }
        )
    }

    return (
        <Card>
            <Row>
                <CardTitle>Products ({products.length})</CardTitle>
                {run.edited && !draft && <Badge $tone="yellow">Edited</Badge>}
            </Row>
            <Muted>Check names, prices and sizes before pushing. Edits are saved on the service until the next parse.</Muted>
            {duplicates.length > 0 && (
                <Banner $tone="warn">
                    <p>
                        {duplicates.length === 1 ? 'One name appears' : `${duplicates.length} names appear`} more than once,
                        and V3 keeps only the last of each: {duplicates.join(', ')}. Rename or remove the extras.
                    </p>
                </Banner>
            )}
            <ProductsTable products={products} onChange={setDraft} readOnly={!!run.busy || save.isPending} />
            {draft && (
                <SaveBar role="region" aria-label="Unsaved product changes">
                    <span>Unsaved changes</span>
                    <SecondaryButton type="button" disabled={save.isPending} onClick={() => setDraft(null)}>
                        Discard
                    </SecondaryButton>
                    <PrimaryButton type="button" disabled={save.isPending} onClick={saveDraft}>
                        {save.isPending ? 'Saving…' : 'Save'}
                    </PrimaryButton>
                </SaveBar>
            )}
        </Card>
    )
}

const PushCard = ({ brand, run, dirty }: { brand: DroplistBrand; run: DroplistRun; dirty: boolean }) => {
    const push = usePushToV3(brand.id)
    const [confirming, setConfirming] = useState(false)
    const count = run.products.length
    const products = count === 1 ? 'this product' : `these ${count} products`
    const ready = count > 0 && !dirty && !run.busy

    const doPush = () => {
        setConfirming(false)
        push.mutate(
            { runId: run.id, confirm: brand.collection },
            { onError: (err) => notify(droplistError(err), 'error') }
        )
    }

    const effect = brand.replaceOnPush ? (
        <>
            Replaces everything in <Collection>{brand.collection}</Collection> with {products}. Last drop&apos;s products are deleted.
        </>
    ) : (
        <>
            Adds {products} to <Collection>{brand.collection}</Collection>, overwriting any with the same name. Nothing else is
            removed.
        </>
    )

    return (
        <Card>
            <CardTitle>Push to V3</CardTitle>
            <Muted>{effect}</Muted>
            {run.pushedAt && (
                <Banner $tone="success">
                    <p>
                        Pushed {when(run.pushedAt)}
                        {run.pushResult &&
                            ` · ${run.pushResult.written} written${run.pushResult.deleted ? `, ${run.pushResult.deleted} removed` : ''}`}
                    </p>
                </Banner>
            )}
            {dirty && <ErrorText>Save or discard your product changes first.</ErrorText>}
            <Row>
                <DangerButton type="button" disabled={!ready || push.isPending} onClick={() => setConfirming(true)}>
                    {run.busy === 'pushing' ? 'Pushing…' : `Push ${count} ${count === 1 ? 'product' : 'products'}`}
                </DangerButton>
            </Row>
            <ConfirmDialog
                open={confirming}
                title={`Push to ${brand.name} V3?`}
                onContinue={doPush}
                onCancel={() => setConfirming(false)}
            >
                {effect}
            </ConfirmDialog>
        </Card>
    )
}

const ReleaseCard = ({ brand, run, dirty }: { brand: DroplistBrand; run: DroplistRun; dirty: boolean }) => {
    const fields = brand.releaseFields ?? []
    const create = useCreateDroplistRelease(brand.id)
    const [config, setConfig] = useState<DroplistConfig>(() => initialConfig(fields, run.releaseConfig))
    const [confirming, setConfirming] = useState(false)
    const count = run.products.length

    // The form's own submit only opens the dialog, so required fields are still checked by the browser.
    const submit = (e: FormEvent) => {
        e.preventDefault()
        setConfirming(true)
    }

    const doCreate = () => {
        setConfirming(false)
        create.mutate(
            { runId: run.id, config: configToSend(fields, config) },
            { onError: (err) => notify(droplistError(err), 'error') }
        )
    }

    const disabled = dirty || !!run.busy || create.isPending || count === 0

    return (
        <Card as="form" onSubmit={submit}>
            <CardTitle>Create release</CardTitle>
            <Muted>
                Uploads each product image and creates an unpublished release with US, EU and JP drops. Times are in your
                local time zone.
            </Muted>
            {run.releasedAt && (
                <Banner $tone="success">
                    <p>Release created {when(run.releasedAt)}. Publish it from the release tools.</p>
                </Banner>
            )}
            <ConfigFields fields={fields} value={config} onChange={setConfig} />
            <Row>
                <PrimaryButton type="submit" disabled={disabled}>
                    {run.busy === 'creating release' ? 'Creating…' : 'Create release'}
                </PrimaryButton>
            </Row>
            <ConfirmDialog
                open={confirming}
                title="Create release?"
                onContinue={doCreate}
                onCancel={() => setConfirming(false)}
            >
                Uploads {count} {count === 1 ? 'image' : 'images'} and creates the unpublished release &ldquo;
                {String(config.title ?? '')}&rdquo;.
            </ConfirmDialog>
        </Card>
    )
}

const BrandTab = ({ brand }: { brand: DroplistBrand }) => {
    const { data: run, isLoading, error } = useDroplistRun(brand.id)
    // Unsaved product edits; null when the table shows the saved products.
    const [draft, setDraft] = useState<DroplistProduct[] | null>(null)
    const runId = run?.id
    useEffect(() => setDraft(null), [runId])

    if (isLoading) return <InlineSpinner />
    if (error) return <ErrorText>{droplistError(error)}</ErrorText>

    const ready = run && run.status === 'ready'

    return (
        <>
            {run?.lastError && !run.busy && (
                <Banner $tone="error">
                    <p>{run.lastError}</p>
                </Banner>
            )}
            {/* Keyed by run so the form picks up the config of a run started elsewhere */}
            <ParseCard key={`parse-${runId}`} brand={brand} run={run ?? null} />
            {run && <RunLog run={run} />}
            {ready && <ProductsCard brand={brand} run={run} draft={draft} setDraft={setDraft} />}
            {ready && <PushCard brand={brand} run={run} dirty={!!draft} />}
            {ready && brand.releaseFields && <ReleaseCard key={`release-${runId}`} brand={brand} run={run} dirty={!!draft} />}
        </>
    )
}

export default BrandTab
